import { useEffect } from "react";
import { Box, Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { formatEffectiveDate, getPrivacyEffectiveDate, PRIVACY_CONTACT_EMAIL } from "../constants/privacy";

// Service-wide privacy policy for nosi.io, served at /privacy without an
// account. Statements describe the service's actual behavior; update this
// page (and the effective date) whenever data practices or integrations change.

const Section = ({ id, title, children }) => (
    <Box component="section" aria-labelledby={id} sx={{ mt: '32px' }}>
        <Typography id={id} component="h2" variant="h2" sx={{ mb: '10px' }}>{title}</Typography>
        {children}
    </Box>
);

const P = ({ children }) => <Typography sx={{ mb: '12px', lineHeight: 1.65 }}>{children}</Typography>;

const List = ({ children }) => (
    <Box component="ul" sx={{ m: 0, mb: '12px', pl: '22px', '& li': { mb: '8px', lineHeight: 1.65 } }}>{children}</Box>
);

const Term = ({ children }) => <Box component="strong" sx={{ fontWeight: 650 }}>{children}</Box>;

const ExternalLink = ({ href, children }) => (
    <Link href={href} target="_blank" rel="noopener noreferrer">{children}<Box component="span" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}> (opens in a new tab)</Box></Link>
);

const Email = () => <Link href={`mailto:${PRIVACY_CONTACT_EMAIL}`}>{PRIVACY_CONTACT_EMAIL}</Link>;

export const PrivacyPolicy = () => {
    const effectiveDate = getPrivacyEffectiveDate();

    useEffect(() => {
        const previous = document.title;
        document.title = 'Privacy Policy · nosi.io';
        return () => { document.title = previous; };
    }, []);

    return (
        <Box component="article" aria-labelledby="privacy-title" sx={{ maxWidth: '720px', mx: 'auto', pb: '32px' }}>
            <Typography id="privacy-title" component="h1" variant="h1">Privacy Policy</Typography>
            <Typography color="text.secondary" sx={{ mt: '8px' }}>
                Effective <Box component="time" dateTime={effectiveDate}>{formatEffectiveDate(effectiveDate)}</Box>
            </Typography>

            <Box sx={{ mt: '24px' }}>
                <P>
                    nosi.io is a place to keep track of the books, movies, places, and other things you experience, and to share
                    them with others. This policy explains what information the nosi.io website and service collect, how that
                    information is used and shared, and the choices you have. It applies to nosi.io as a whole, including any
                    sign-in or other integrations we offer.
                </P>
            </Box>

            <Section id="who-we-are" title="Who we are">
                <P>
                    nosi.io is a personal project operated by Terence Ramos, based in California, United States. For privacy
                    questions or requests, contact <Email />.
                </P>
            </Section>

            <Section id="information-we-collect" title="Information we collect">
                <P><Term>Information you give us</Term></P>
                <List>
                    <li><Term>Account details:</Term> your username, email address, and password when you sign up, plus the referral code used to join. Passwords are stored only as a one-way hash.</li>
                    <li><Term>Profile information:</Term> a profile photo if you add one, and your profile visibility setting.</li>
                    <li><Term>Your content and activity:</Term> the items you add or suggest edits to, the experiences you record (such as "read" or "visited") and when, photos you upload, comments, likes, and friend requests and connections.</li>
                    <li><Term>Messages to us:</Term> anything you send when you contact us.</li>
                </List>
                <P><Term>Information collected automatically</Term></P>
                <List>
                    <li><Term>Sign-in and device information:</Term> when you sign in, we store a randomly generated device identifier and your browser's user-agent details with your session so you can stay signed in and manage sessions.</li>
                    <li><Term>Upload information:</Term> when you upload a photo, we record the IP address and browser user-agent details of the request to help prevent abuse.</li>
                    <li><Term>Usage information:</Term> we use Google Analytics to understand how the website is used (see "Analytics" below).</li>
                    <li><Term>Server logs:</Term> like most websites, our servers and hosting provider may log technical details of requests, such as IP address, time, and pages requested.</li>
                </List>
                <P>
                    Location information on nosi.io, such as the location of a place, is information about an item that someone
                    entered. We do not collect your device's location.
                </P>
            </Section>

            <Section id="how-we-use-information" title="How we use information">
                <List>
                    <li>To create and run your account, sign you in, and keep your session secure.</li>
                    <li>To show your collection, experiences, comments, and profile to you and to the people allowed to see them.</li>
                    <li>To provide features such as search, friends, likes, comments, and suggested edits to items.</li>
                    <li>To send account emails, such as email verification and password reset codes.</li>
                    <li>To understand how nosi.io is used and to improve it.</li>
                    <li>To prevent abuse, investigate problems, and protect the service and its users.</li>
                    <li>To comply with legal obligations.</li>
                </List>
                <P>
                    We do not sell your personal information, we do not share it for advertising, and we do not use it to train
                    artificial intelligence (AI) models.
                </P>
            </Section>

            <Section id="what-others-can-see" title="What other people can see">
                <P>nosi.io is a social service, so some information is visible to others:</P>
                <List>
                    <li><Term>Items</Term> you add to nosi.io, and edits you suggest, become part of the shared catalog that anyone can view.</li>
                    <li><Term>Comments</Term> you post on items are visible to anyone who can view the item, along with your username. Likes are counted publicly.</li>
                    <li>
                        <Term>Your profile and activity</Term> (your collection, experiences, and recent activity) follow your
                        visibility setting. <Term>Public</Term> lets anyone see them, including people who are not signed in.{' '}
                        <Term>Friends</Term> limits them to you and your friends, and is the default for new accounts.{' '}
                        <Term>Private</Term> limits them to you. You can change this in your profile settings.
                    </li>
                    <li>If you share a link to your profile, anyone with the link can open it, subject to your visibility setting.</li>
                </List>
                <P>The operator of nosi.io can access account information and content as needed to run and support the service.</P>
            </Section>

            <Section id="cookies-and-storage" title="Cookies and browser storage">
                <List>
                    <li>A secure, HTTP-only cookie keeps you signed in between visits.</li>
                    <li>Your browser's local storage holds your sign-in token, basic account details, a device identifier, and simple preferences (such as whether you have seen the welcome message).</li>
                    <li>Google Analytics sets its own cookies to measure website use.</li>
                </List>
                <P>
                    You can clear cookies and local storage in your browser settings. Doing so signs you out, and blocking them
                    may prevent sign-in from working.
                </P>
            </Section>

            <Section id="service-providers" title="Service providers and sharing">
                <P>We share information only with providers that help run nosi.io, and only as needed for them to do that work:</P>
                <List>
                    <li><Term>Amazon Web Services (AWS)</Term> hosts our servers, database, and uploaded photos in the United States.</li>
                    <li><Term>Google Analytics</Term> receives usage information from the website (see below).</li>
                    <li><Term>Mapbox</Term> provides the maps shown for places. When a map loads, your browser requests map data directly from Mapbox, which receives your IP address and browser details.</li>
                    <li><Term>Email delivery provider:</Term> the address and content of account emails we send you pass through our email service.</li>
                    <li><Term>Google Fonts</Term> serves some of the website's fonts. Your browser requests them directly from Google, which receives your IP address and browser details.</li>
                </List>
                <P>
                    We may also disclose information if required by law, or when necessary to protect the rights, safety, or
                    property of nosi.io, its users, or others.
                </P>
                <P>
                    If we add sign-in with another provider (such as Google) or another integration, we will describe the
                    information it involves in this policy before offering it.
                </P>
            </Section>

            <Section id="analytics" title="Analytics">
                <P>
                    nosi.io uses Google Analytics, a service provided by Google, to understand how visitors use the website.
                    Google Analytics collects information such as the pages you visit, how you interact with them, your
                    approximate location based on your IP address, and your browser and device type, using cookies and similar
                    technology. Learn how Google uses this information at{' '}
                    <ExternalLink href="https://policies.google.com/technologies/partner-sites">How Google uses information from sites that use its services</ExternalLink>.
                    You can opt out with the{' '}
                    <ExternalLink href="https://tools.google.com/dlpage/gaoptout">Google Analytics Opt-out Browser Add-on</ExternalLink>.
                </P>
            </Section>

            <Section id="retention" title="How long we keep information">
                <P>
                    We keep your account information and content while your account exists. We do not currently have a fixed
                    schedule for deleting logs or backups, so some information may remain in them after it is deleted
                    elsewhere. Information you shared publicly, such as items you added and comments, may remain visible where
                    other people rely on it unless you ask us to remove it.
                </P>
            </Section>

            <Section id="your-choices" title="Your choices and requests">
                <List>
                    <li>You can change your profile visibility and profile photo in your profile settings.</li>
                    <li>You can remove likes you have made.</li>
                    <li>
                        There is not yet a way to delete your account yourself. To ask for a copy of your information, a
                        correction, or deletion of your account or content, email <Email /> from the address on your account. We
                        handle these requests manually and may need to confirm that the request comes from you.
                    </li>
                </List>
                <P>
                    Depending on where you live, you may have additional rights over your personal information. Contact us to
                    exercise them.
                </P>
            </Section>

            <Section id="security" title="Security">
                <P>
                    We use reasonable measures to protect your information, such as encrypted connections, hashed passwords,
                    and secure sign-in cookies. No website or service is completely secure, so we cannot guarantee the security
                    of your information.
                </P>
            </Section>

            <Section id="children" title="Children">
                <P>
                    nosi.io is not intended for children under 13, and you must be at least 13 years old to use it. If you
                    believe a child under 13 has given us personal information, contact us at <Email /> and we will take steps
                    to delete it.
                </P>
            </Section>

            <Section id="international" title="International users">
                <P>
                    nosi.io is operated from the United States, and information is stored and processed in the United States.
                    If you use nosi.io from another country, your information will be transferred to the United States, where
                    data protection laws may differ from those in your country.
                </P>
            </Section>

            <Section id="changes" title="Changes to this policy">
                <P>
                    We will update this page when our practices change and revise the effective date above. For material
                    changes, we will also post a notice on the website.
                </P>
            </Section>

            <Section id="contact" title="Contact">
                <P>
                    Questions or requests about privacy: <Email />.
                </P>
                <P>
                    <Link component={RouterLink} to="/">Back to nosi.io</Link>
                </P>
            </Section>
        </Box>
    );
}
